import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { canEdit, getUserType } from "../permissions";
import API_BASE from "../config";

const Bookings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userType = getUserType();
  const allowEdit = canEdit(userType, "bookings");
  const [bookingData, setBookingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [project, setProject] = useState("");
  const [paymentSource, setPaymentSource] = useState("");
  const [registry, setRegistry] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [amountSort, setAmountSort] = useState("");
  const [dateSort, setDateSort] = useState("");
  const [page, setPage] = useState(1);
  const [projectsMap, setProjectsMap] = useState({});

  const perPage = 10;

  // Fetch bookings from API
  useEffect(() => {
    if (location.state && location.state.selectedStatus) {
      setStatusFilter(location.state.selectedStatus);
    }
    setLoading(true);
    setError("");

    fetch(`${API_BASE}/api/allrows`, {
      method: "POST",
      headers: {
        Authorization: "1",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        token: "3232",
        table: "bookings",
      }),
    })
      .then((res) => res.json())
      .then((json) => {
        setLoading(false);
        if (json.status == true && Array.isArray(json.data)) {
          setBookingData(json.data);
        } else if (Array.isArray(json.data)) {
          setBookingData(json.data);
        } else {
          setError("Failed to load bookings.");
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error("Bookings API Error:", err);
        setError("An error occurred while fetching bookings.");
      });

    // Fetch projects table to map shortform → full name
    fetch(`${API_BASE}/api/allrows`, {
      method: "POST",
      headers: {
        Authorization: "1",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        token: "3232",
        table: "projects",
      }),
    })
      .then((res) => res.json())
      .then((json) => {
        if (Array.isArray(json.data)) {
          const map = {};
          json.data.forEach((p) => {
            if (p.shortform) {
              map[p.shortform] = p.name || p.shortform;
            }
          });
          setProjectsMap(map);
        }
      })
      .catch((err) => console.error("Projects API Error:", err));
  }, []);

  // Get unique project short forms for dropdown
  const projectShortForms = [...new Set(bookingData.map((b) => b.project_name).filter(Boolean))];

  /* ===== FILTER LOGIC ===== */
  let filtered = bookingData.filter((item) =>
    Object.values(item).join(" ").toLowerCase().includes(search.toLowerCase())
  );

  if (project) filtered = filtered.filter((i) => i.project_name === project);
  if (paymentSource) filtered = filtered.filter((i) => i.payment_source === paymentSource);
  if (registry) filtered = filtered.filter((i) => i.r_status === registry);
  if (statusFilter) filtered = filtered.filter((i) => i.status === statusFilter);

  if (amountSort === "asc") filtered.sort((a, b) => Number(a.total_saleprice) - Number(b.total_saleprice));
  if (amountSort === "desc") filtered.sort((a, b) => Number(b.total_saleprice) - Number(a.total_saleprice));

  // Helper to parse DD/MM/YYYY dates
  const parseDate = (dateStr) => {
    if (!dateStr) return 0;
    const parts = dateStr.split("/");
    if (parts.length === 3) {
      // Create date format: YYYY, MM-1, DD
      return new Date(parts[2], parts[1] - 1, parts[0]).getTime();
    }
    // Fallback if Date is already ISO or another format
    return new Date(dateStr).getTime() || 0;
  };

  if (dateSort === "asc") filtered.sort((a, b) => parseDate(a.date) - parseDate(b.date));
  if (dateSort === "desc") filtered.sort((a, b) => parseDate(b.date) - parseDate(a.date));

  /* ===== PAGINATION ===== */
  const totalPages = Math.ceil(filtered.length / perPage);
  const data = filtered.slice((page - 1) * perPage, page * perPage);

  const formatAmount = (amt) => {
    const num = Number(amt);
    if (isNaN(num) || num === 0) return "—";
    return "₹" + num.toLocaleString("en-IN");
  };

  return (
    <>
      <style>
        {`
        .bk-page {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .bk-page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }

        .bk-page-title {
          font-size: 22px;
          font-weight: 700;
          color: #0f172a;
        }

        .bk-page-count {
          font-size: 13px;
          color: #64748b;
          margin-top: 2px;
        }

        .bk-filter-bar {
          background: #ffffff;
          padding: 16px;
          border-radius: 14px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: center;
        }

        .bk-filter-bar select,
        .bk-filter-bar input {
          padding: 10px 14px;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          outline: none;
          min-width: 150px;
          font-size: 13px;
          font-family: 'Inter', sans-serif;
          color: #334155;
          background: #f8fafc;
          transition: all 0.2s;
        }

        .bk-filter-bar select:focus,
        .bk-filter-bar input:focus {
          border-color: #93c5fd;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.08);
          background: #fff;
        }

        .bk-filter-bar input {
          flex: 1;
          min-width: 200px;
        }

        .bk-table-wrap {
          background: #ffffff;
          border-radius: 14px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
          overflow-x: auto;
        }

        .bk-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 900px;
        }

        .bk-table th,
        .bk-table td {
          padding: 13px 14px;
          font-size: 13px;
          text-align: left;
          border-bottom: 1px solid #f1f5f9;
        }

        .bk-table th {
          background: #f8fafc;
          color: #64748b;
          font-weight: 600;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          white-space: nowrap;
          position: sticky;
          top: 0;
        }

        .bk-table tbody tr {
          transition: background 0.15s;
        }

        .bk-table tbody tr:hover {
          background: #f8fafc;
        }

        .bk-table td {
          color: #334155;
          font-weight: 500;
        }

        .bk-customer-cell {
          display: flex;
          flex-direction: column;
          gap: 1px;
        }

        .bk-customer-name {
          font-weight: 600;
          color: #0f172a;
          text-transform: capitalize;
        }

        .bk-customer-phone {
          font-size: 11px;
          color: #94a3b8;
          font-weight: 400;
        }

        .bk-unit-cell {
          display: flex;
          flex-direction: column;
          gap: 1px;
        }

        .bk-unit-main {
          font-weight: 600;
          color: #2563eb;
        }

        .bk-unit-extra {
          font-size: 11px;
          color: #94a3b8;
        }

        .bk-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
          white-space: nowrap;
        }

        .bk-badge-done {
          background: #ecfdf5;
          color: #059669;
          border: 1px solid #a7f3d0;
        }

        .bk-badge-pending {
          background: #fffbeb;
          color: #d97706;
          border: 1px solid #fcd34d;
        }

        .bk-badge-applied {
          background: #eff6ff;
          color: #2563eb;
          border: 1px solid #bfdbfe;
        }

        .bk-badge-na {
          background: #f8fafc;
          color: #94a3b8;
          border: 1px solid #e2e8f0;
        }

        .bk-amount {
          font-weight: 700;
          color: #0f172a;
          font-variant-numeric: tabular-nums;
        }

        .bk-date {
          color: #64748b;
          font-size: 12px;
          white-space: nowrap;
        }

        /* Pagination */
        .bk-pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }

        .bk-pagination-info {
          font-size: 13px;
          color: #64748b;
        }

        .bk-pagination-btns {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .bk-pagination-btns button {
          padding: 8px 14px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: #fff;
          font-size: 13px;
          font-weight: 500;
          color: #334155;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'Inter', sans-serif;
        }

        .bk-pagination-btns button:hover:not(:disabled) {
          background: #f1f5f9;
          border-color: #cbd5e1;
        }

        .bk-pagination-btns button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .bk-pagination-btns span {
          font-size: 13px;
          color: #64748b;
          padding: 0 6px;
        }

        /* Loading & Error */
        .bk-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          gap: 14px;
        }

        .bk-spinner {
          width: 36px;
          height: 36px;
          border: 3px solid #e2e8f0;
          border-top-color: #2563eb;
          border-radius: 50%;
          animation: bk-spin 0.7s linear infinite;
        }

        @keyframes bk-spin {
          to { transform: rotate(360deg); }
        }

        .bk-loading-text {
          font-size: 14px;
          color: #64748b;
        }

        .bk-error {
          padding: 16px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 12px;
          color: #dc2626;
          font-size: 14px;
          text-align: center;
        }

        .bk-empty {
          padding: 40px 20px;
          text-align: center;
          color: #94a3b8;
          font-size: 14px;
        }

        .bk-edit-btn {
          padding: 6px 14px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: #f8fafc;
          color: #2563eb;
          cursor: pointer;
          font-size: 12px;
          font-weight: 600;
          font-family: 'Inter', sans-serif;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .bk-edit-btn:hover {
          background: #eff6ff;
          border-color: #93c5fd;
          box-shadow: 0 2px 8px rgba(37,99,235,0.12);
        }

        /* Responsive */
        @media (max-width: 640px) {
          .bk-filter-bar {
            flex-direction: column;
            align-items: stretch;
          }

          .bk-filter-bar input,
          .bk-filter-bar select {
            min-width: 100%;
          }

          .bk-pagination {
            flex-direction: column;
            align-items: stretch;
            text-align: center;
          }

          .bk-pagination-btns {
            justify-content: center;
          }
        }
        `}
      </style>

      <div className="bk-page">
        {/* Header */}
        <div className="bk-page-header">
          <div>
            <h2 className="bk-page-title">📋 Bookings</h2>
            <div className="bk-page-count">
              {loading ? "Loading..." : `${filtered.length} total bookings`}
            </div>
          </div>
          <div className="bk-actions">
            <button className="bk-btn bk-btn-primary" onClick={() => navigate("/dashboard/bookings/create")}>
              ➕ Create Booking
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bk-filter-bar">
          <input
            placeholder="🔍 Search by name, unit, phone..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />

          <select value={project} onChange={(e) => { setProject(e.target.value); setPage(1); }}>
            <option value="">All Projects</option>
            {projectShortForms.map((sf) => (
              <option key={sf} value={sf}>{projectsMap[sf] || sf}</option>
            ))}
          </select>

          <select value={paymentSource} onChange={(e) => { setPaymentSource(e.target.value); setPage(1); }}>
            <option value="">Payment Source</option>
            <option value="100% Self">100% Self</option>
            <option value="Self + Bank Loan">Self + Bank Loan</option>
          </select>

          
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="">All Status</option>
            <option value="Booked">Booked</option>
            <option value="Cancel">Cancel</option>
            <option value="Document Pending">Document Pending</option>
            <option value="File Submit">File Submit</option>
            <option value="Loan Sanctioned">Loan Sanctioned</option>
            <option value="Registry Scheduled">Registry Scheduled</option>
            <option value="Registry Done">Registry Done</option>
            <option value="Construction Delay">Construction Delay</option>
            <option value="Under Construction">Under Construction</option>
            <option value="Construction Complete">Construction Complete</option>
            <option value="Checklist Signed">Checklist Signed</option>
            <option value="Possesion Letter Issued">Possesion Letter Issued</option>
          </select>

          <select value={amountSort} onChange={(e) => setAmountSort(e.target.value)}>
            <option value="">Sort by Amount</option>
            <option value="asc">Low → High</option>
            <option value="desc">High → Low</option>
          </select>

          <select value={dateSort} onChange={(e) => setDateSort(e.target.value)}>
            <option value="">Sort by Date</option>
            <option value="asc">Oldest First</option>
            <option value="desc">Newest First</option>
          </select>
        </div>

        {/* Error */}
        {error && <div className="bk-error">⚠️ {error}</div>}

        {/* Loading */}
        {loading ? (
          <div className="bk-loading">
            <div className="bk-spinner" />
            <div className="bk-loading-text">Loading bookings...</div>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="bk-table-wrap">
              <table className="bk-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Unit</th>
                    <th>Project</th>
                    <th>Sale Type</th>
                    <th>Total Amount</th>
                    <th>Payment</th>
                    <th>Registry</th>
                    <th>Status</th>
                    <th>Loan</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan="10">
                        <div className="bk-empty">No bookings found</div>
                      </td>
                    </tr>
                  ) : (
                    data.map((b) => (
                      <tr key={b.id}>
                        <td>
                          <div className="bk-customer-cell">
                            <span className="bk-customer-name">{b.customer_name}</span>
                            <span className="bk-customer-phone">{b.cust_phone}</span>
                          </div>
                        </td>
                        <td>
                          <div className="bk-unit-cell">
                            <span className="bk-unit-main">{b.unit}</span>
                            {b.unit2 && <span className="bk-unit-extra">+{b.unit2}</span>}
                          </div>
                        </td>
                        <td>{b.project_name}</td>
                        <td>{b.sale_type || "—"}</td>
                        <td><span className="bk-amount">{formatAmount(b.total_saleprice)}</span></td>
                        <td>
                          <span className={`bk-badge ${b.payment_source === "100% Self" ? "bk-badge-done" :
                            b.payment_source === "100% Bank Loan" ? "bk-badge-applied" :
                              "bk-badge-pending"
                            }`}>
                            {b.payment_source || "—"}
                          </span>
                        </td>
                        <td>
                          <span className={`bk-badge ${b.r_status === "Done" ? "bk-badge-done" :
                            b.r_status === "Not Done" ? "bk-badge-pending" :
                              "bk-badge-na"
                            }`}>
                            {b.r_status || "—"}
                          </span>
                        </td>
                        <td>
                          <span className={`bk-badge ${
                            b.status === "Booked" ? "bk-badge-applied" : 
                            b.status === "cancel" ? "bk-badge-na" : 
                            b.status === "Loan Sanctioned" || b.status === "Registry Done" ? "bk-badge-done" : 
                            "bk-badge-pending"
                          }`}>
                            {b.status || "—"}
                          </span>
                        </td>
                        <td>
                          <span className={`bk-badge ${b.loan_status === "Applied" ? "bk-badge-applied" :
                            b.loan_status === "Approved" ? "bk-badge-done" :
                              "bk-badge-na"
                            }`}>
                            {b.loan_status || "N/A"}
                          </span>
                        </td>
                        <td><span className="bk-date">{b.date || "—"}</span></td>
                        <td>
                          <button
                            className="bk-edit-btn"
                            onClick={() => navigate("/dashboard/bookings/view", { state: { booking: b } })}
                          >
                            👁️ View More
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bk-pagination">
                <div className="bk-pagination-info">
                  Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length}
                </div>
                <div className="bk-pagination-btns">
                  <button disabled={page === 1} onClick={() => setPage(1)}>First</button>
                  <button disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
                  <span>Page {page} of {totalPages}</span>
                  <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
                  <button disabled={page === totalPages} onClick={() => setPage(totalPages)}>Last</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Bookings;
